import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import { reservationPhoneDigits } from '@/domain/reservations/reservation-phone';
import type { IReservationGuest } from '@/interfaces/IReservationGuest';

export function useReservationGuestLookup(
  scope: () => { restaurantId?: number; branchId?: number }
) {
  const query = ref('');
  const matches = shallowRef<IReservationGuest[]>([]);
  const loading = ref(false);
  const failed = ref(false);
  function searchable(value: string) {
    return (
      reservationPhoneDigits(value).length >= 4 ||
      value.replace(/[^\p{L}]/gu, '').length >= 4
    );
  }
  const ready = computed(() => searchable(query.value));
  let alive = true;
  let sequence = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  async function search() {
    if (timer) clearTimeout(timer);
    const request = ++sequence;
    const { restaurantId, branchId } = scope();
    matches.value = [];
    failed.value = false;
    if (!alive || !restaurantId || !searchable(query.value)) {
      loading.value = false;
      return;
    }
    loading.value = true;
    const text = query.value.trim();
    timer = setTimeout(async () => {
      timer = undefined;
      try {
        const result = await ReservationsService.searchGuests(restaurantId, {
          search: text,
        });
        if (
          !alive ||
          request !== sequence ||
          scope().restaurantId !== restaurantId ||
          scope().branchId !== branchId
        )
          return;
        matches.value = result.items;
        loading.value = false;
      } catch {
        if (
          !alive ||
          request !== sequence ||
          scope().restaurantId !== restaurantId ||
          scope().branchId !== branchId
        )
          return;
        failed.value = true;
        loading.value = false;
      }
    }, 300);
  }
  watch(
    () => [query.value, scope().restaurantId, scope().branchId],
    () => void search(),
    { flush: 'sync' }
  );
  onScopeDispose(() => {
    alive = false;
    sequence++;
    if (timer) clearTimeout(timer);
  });
  return { query, matches, loading, failed, ready, search };
}
